import { useState } from 'react';
import { passwordSchema } from 'shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const EMPTY = { currentPassword: '', newPassword: '', confirmPassword: '' };

export function ChangePassword() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  /**
   * Validated with the same schema the server uses, so the rule and its wording come
   * from one place — a client-only copy would drift from what the API enforces.
   */
  const strength = passwordSchema.safeParse(form.newPassword);
  const lengthError = form.newPassword && !strength.success
    ? strength.error.issues[0]?.message ?? null
    : null;
  const mismatch = Boolean(form.confirmPassword) && form.newPassword !== form.confirmPassword;
  const reused = Boolean(form.newPassword) && form.newPassword === form.currentPassword;

  const blocked = Boolean(lengthError) || mismatch || reused;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);

    if (mismatch) {
      setMsg({ kind: 'error', text: 'Konfirmasi password tidak sama dengan password baru.' });
      return;
    }
    if (!strength.success) {
      setMsg({ kind: 'error', text: strength.error.issues[0]?.message ?? 'Password baru tidak valid.' });
      return;
    }

    setBusy(true);
    try {
      const r = await api.post<{ message: string }>('/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      setForm(EMPTY);
      setMsg({ kind: 'ok', text: r.message });
    } catch (err) {
      // A wrong current password comes back as 400, so the session survives the attempt.
      setMsg({ kind: 'error', text: err instanceof ApiError ? err.message : 'Gagal mengubah password.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <h2>Ganti Password</h2>
        <p>
          Mengubah password akun <span className="mono">{user?.email}</span>. Sesi yang sedang
          berjalan tidak diputus, jadi Anda tidak perlu masuk ulang.
        </p>
      </div>

      <form onSubmit={submit} className="card stack" style={{ maxWidth: 480 }}>
        {msg && <div className={`msg ${msg.kind}`}>{msg.text}</div>}

        <div className="field">
          <label className="label" htmlFor="cp-current">Password saat ini</label>
          <input
            id="cp-current" type="password" required autoComplete="current-password"
            value={form.currentPassword} onChange={set('currentPassword')}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="cp-new">Password baru</label>
          <input
            id="cp-new" type="password" required autoComplete="new-password"
            value={form.newPassword} onChange={set('newPassword')}
            aria-invalid={Boolean(lengthError) || reused}
            aria-describedby="cp-new-hint"
          />
          <span id="cp-new-hint" className="muted" style={{ fontSize: 12.5 }}>
            {lengthError ?? (reused ? 'Password baru harus berbeda dari password saat ini.' : 'Minimal 8 karakter.')}
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="cp-confirm">Ulangi password baru</label>
          <input
            id="cp-confirm" type="password" required autoComplete="new-password"
            value={form.confirmPassword} onChange={set('confirmPassword')}
            aria-invalid={mismatch}
          />
          {mismatch && (
            <span className="muted" style={{ fontSize: 12.5, color: 'var(--danger)' }}>
              Belum sama dengan password baru.
            </span>
          )}
        </div>

        <div>
          <button type="submit" className="primary" disabled={busy || blocked}>
            {busy ? 'Menyimpan…' : 'Ubah password'}
          </button>
        </div>
      </form>
    </div>
  );
}
